/**
 * Admin Prompts Page - AI Prompts Repository Management
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { 
  Search, 
  Plus, 
  MoreVertical,
  Edit2,
  Trash2,
  Copy,
  MessageSquare,
  CheckCircle,
  Archive,
  ArchiveRestore,
  Star,
  GitBranch,
  SlidersHorizontal,
  Play,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  usePrompts, 
  useDeletePrompt,
  useRestorePrompt,
  useCreatePrompt,
  useChangePromptStatus,
  useSetDefaultPrompt,
  useDuplicatePrompt,
  type Prompt,
  type PromptStatus,
} from '@/hooks/use-prompts';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

// Prompt categories
const PROMPT_CATEGORIES = [
  { value: 'rag', label: 'RAG' },
  { value: 'agent', label: 'Agent' },
  { value: 'product_guru', label: 'Product Guru' },
  { value: 'discovery', label: 'Discovery' },
  { value: 'definition', label: 'Definition' },
  { value: 'design', label: 'Design' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'general', label: 'General' },
];

const STATUS_LABELS: Record<PromptStatus, string> = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  DEPRECATED: 'Deprecated',
};

const STATUS_COLORS: Record<PromptStatus, { bg: string; text: string; border: string }> = {
  DRAFT: { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' },
  ACTIVE: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  DEPRECATED: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
};

// Status badge component
function StatusBadge({ status }: { status: PromptStatus }) {
  const colors = STATUS_COLORS[status];
  const label = STATUS_LABELS[status];
  
  const icons: Record<PromptStatus, React.ReactNode> = {
    ACTIVE: <CheckCircle className="w-3.5 h-3.5" />,
    DRAFT: <Edit2 className="w-3.5 h-3.5" />,
    DEPRECATED: <Archive className="w-3.5 h-3.5" />,
  };

  return (
    <span className={cn(
      'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border',
      colors.bg,
      colors.text,
      colors.border
    )}>
      {icons[status]}
      {label}
    </span>
  );
}

export default function AdminPromptsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  
  const showArchived = searchParams.get('showArchived') === 'true';
  const setShowArchived = (value: boolean) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('showArchived', 'true');
    } else {
      newParams.delete('showArchived');
    }
    setSearchParams(newParams, { replace: true });
  };
  
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; prompt: Prompt | null }>({ open: false, prompt: null });
  const [createModal, setCreateModal] = useState(false);
  const [duplicateModal, setDuplicateModal] = useState<{ open: boolean; prompt: Prompt | null }>({ open: false, prompt: null });
  
  const { toast } = useToast();

  const { data, isLoading, isError, refetch } = usePrompts({
    search: searchQuery || undefined,
    category: categoryFilter || undefined,
    status: statusFilter || undefined,
    showArchived: showArchived,
  });

  const createPrompt = useCreatePrompt();
  const deletePrompt = useDeletePrompt();
  const restorePrompt = useRestorePrompt();
  const changeStatus = useChangePromptStatus();
  const setDefault = useSetDefaultPrompt();
  const duplicatePromptMutation = useDuplicatePrompt();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (openMenuId && !(e.target as Element).closest('.actions-menu')) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openMenuId]);

  const prompts = data?.prompts || [];

  // Create prompt form state
  const [newPrompt, setNewPrompt] = useState({
    key: '',
    name: '',
    category: 'general',
    systemPrompt: '',
  });

  // Duplicate prompt form state
  const [duplicateForm, setDuplicateForm] = useState({
    newKey: '',
    newName: '',
  });

  const handleCreate = async () => {
    if (!newPrompt.key || !newPrompt.name || !newPrompt.systemPrompt) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    try {
      await createPrompt.mutateAsync(newPrompt);
      toast({
        title: 'Prompt Created',
        description: `${newPrompt.name} has been created successfully`,
      });
      setCreateModal(false);
      setNewPrompt({ key: '', name: '', category: 'general', systemPrompt: '' });
      refetch();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create prompt',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.prompt) return;

    try {
      await deletePrompt.mutateAsync(deleteModal.prompt.id);
      toast({
        title: 'Prompt Archived',
        description: `${deleteModal.prompt.name} has been archived`,
      });
      setDeleteModal({ open: false, prompt: null });
      refetch();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to archive prompt',
        variant: 'destructive',
      });
    }
  };

  const handleRestore = async (prompt: Prompt) => {
    try {
      await restorePrompt.mutateAsync(prompt.id);
      toast({
        title: 'Prompt Restored',
        description: `${prompt.name} has been restored`,
      });
      refetch();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to restore prompt',
        variant: 'destructive',
      });
    }
  };

  const handleStatusChange = async (prompt: Prompt, newStatus: PromptStatus) => {
    try {
      await changeStatus.mutateAsync({ promptId: prompt.id, status: newStatus });
      toast({
        title: 'Status Updated',
        description: `${prompt.name} is now ${STATUS_LABELS[newStatus]}`,
      });
      refetch();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update status',
        variant: 'destructive',
      });
    }
  };

  const handleSetDefault = async (prompt: Prompt, isDefault: boolean) => {
    try {
      await setDefault.mutateAsync({ promptId: prompt.id, isDefault });
      toast({
        title: isDefault ? 'Set as Default' : 'Default Removed',
        description: `${prompt.name} ${isDefault ? 'is now the default' : 'is no longer the default'} for ${prompt.category}`,
      });
      refetch();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update default',
        variant: 'destructive',
      });
    }
  };

  const handleDuplicate = async () => {
    if (!duplicateModal.prompt || !duplicateForm.newKey) {
      toast({
        title: 'Validation Error',
        description: 'Please provide a new key for the duplicate',
        variant: 'destructive',
      });
      return;
    }

    try {
      await duplicatePromptMutation.mutateAsync({
        promptId: duplicateModal.prompt.id,
        newKey: duplicateForm.newKey,
        newName: duplicateForm.newName || undefined,
      });
      toast({
        title: 'Prompt Duplicated',
        description: `Created copy with key: ${duplicateForm.newKey}`,
      });
      setDuplicateModal({ open: false, prompt: null });
      setDuplicateForm({ newKey: '', newName: '' });
      refetch();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to duplicate prompt',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header - white background extends to right edge */}
      <div className="shrink-0 bg-white border-b border-surface-border">
        <div className="px-8 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-medium text-gray-900 tracking-tight">
                AI Prompts
              </h1>
              <p className="text-base text-gray-500 mt-1">
                Manage system prompts for AI features across the platform
              </p>
            </div>
            <button
              onClick={() => setCreateModal(true)}
              className="h-10 px-4 flex items-center gap-2 bg-brand-700 rounded-lg text-white hover:bg-brand-800 transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span>Create Prompt</span>
            </button>
          </div>

          {/* Search and Filters - matching admin/templates design */}
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search prompts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-[42px] pl-10 pr-4 border border-surface-border rounded-lg text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={cn(
                'flex items-center gap-2 h-[42px] px-4 border rounded-lg transition-colors',
                showFilters
                  ? 'bg-brand-100 border-brand-200 text-brand-800'
                  : 'border-surface-border text-gray-700 hover:bg-surface-secondary'
              )}
            >
              <SlidersHorizontal className="w-5 h-5" />
              Filters
            </button>

            {/* Show Archived Toggle - matching admin/templates */}
            <label className="flex items-center gap-2 h-[42px] px-4 border border-surface-border rounded-lg cursor-pointer hover:bg-surface-secondary transition-colors">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="sr-only peer"
                />
                <div className={cn(
                  "w-9 h-5 rounded-full transition-colors",
                  showArchived ? "bg-brand-700" : "bg-gray-300"
                )} />
                <div className={cn(
                  "absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform",
                  showArchived && "translate-x-4"
                )} />
              </div>
              <span className="text-sm text-gray-700 whitespace-nowrap">Show Archived</span>
            </label>
          </div>

          {/* Filter Dropdowns - matching admin/templates */}
          {showFilters && (
            <div className="mt-4 flex items-center gap-3">
              <Select value={categoryFilter || '__all__'} onValueChange={(v) => setCategoryFilter(v === '__all__' ? '' : v)}>
                <SelectTrigger className="h-[42px] w-[180px] border-surface-border rounded-lg text-gray-700 focus:ring-brand-500/20 focus:border-brand-500">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent className="rounded-lg border-surface-border shadow-dropdown">
                  <SelectItem value="__all__">All Categories</SelectItem>
                  {PROMPT_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter || '__all__'} onValueChange={(v) => setStatusFilter(v === '__all__' ? '' : v)}>
                <SelectTrigger className="h-[42px] w-[180px] border-surface-border rounded-lg text-gray-700 focus:ring-brand-500/20 focus:border-brand-500">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent className="rounded-lg border-surface-border shadow-dropdown">
                  <SelectItem value="__all__">All Statuses</SelectItem>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="DEPRECATED">Deprecated</SelectItem>
                </SelectContent>
              </Select>
              {(categoryFilter || statusFilter) && (
                <button
                  onClick={() => {
                    setCategoryFilter('');
                    setStatusFilter('');
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Content - gray background extends to right edge */}
      <div className="flex-1 overflow-auto bg-surface-secondary">
        <div className="container px-8 py-6">
        {isLoading ? (
          <div className="bg-white border border-surface-border rounded-lg overflow-hidden">
            <div className="p-4 space-y-4">
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <div key={i} className="flex items-center gap-4 p-4 border-b border-gray-100 last:border-0">
                  <Skeleton className="h-5 w-1/4" />
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-5 w-20" />
                  <Skeleton className="h-5 w-24" />
                </div>
              ))}
            </div>
          </div>
        ) : isError ? (
          <div className="bg-white border border-surface-border rounded-lg p-8 text-center">
            <p className="text-red-600">Failed to load prompts. Please try again.</p>
            <button
              onClick={() => refetch()}
              className="mt-4 text-brand-700 hover:underline"
            >
              Retry
            </button>
          </div>
        ) : prompts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full bg-white border-2 border-dashed border-gray-300 rounded-lg py-16">
            <MessageSquare className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No prompts found</h3>
            <p className="text-gray-500">
              {searchQuery || categoryFilter || statusFilter
                ? 'Try adjusting your search or filters'
                : 'Get started by creating your first prompt'}
            </p>
          </div>
        ) : (
          <div className="bg-white border border-surface-border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <span className="text-sm font-medium text-gray-700">Prompt</span>
                  </th>
                  <th className="px-6 py-3 text-left">
                    <span className="text-sm font-medium text-gray-700">Key</span>
                  </th>
                  <th className="px-6 py-3 text-left">
                    <span className="text-sm font-medium text-gray-700">Category</span>
                  </th>
                  <th className="px-6 py-3 text-left">
                    <span className="text-sm font-medium text-gray-700">Status</span>
                  </th>
                  <th className="px-6 py-3 text-left">
                    <span className="text-sm font-medium text-gray-700">Version</span>
                  </th>
                  <th className="px-6 py-3 text-left">
                    <span className="text-sm font-medium text-gray-700">Updated</span>
                  </th>
                  <th className="px-6 py-3 text-right">
                    <span className="text-sm font-medium text-gray-700">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {prompts.map((prompt) => (
                  <tr 
                    key={prompt.id} 
                    className={cn(
                      'hover:bg-gray-50 transition-colors',
                      prompt.isArchived && 'bg-gray-50 opacity-60'
                    )}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center">
                          <MessageSquare className="w-4 h-4 text-brand-700" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{prompt.name}</span>
                            {prompt.isDefault && (
                              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                            )}
                          </div>
                          {prompt.description && (
                            <p className="text-sm text-gray-500 truncate max-w-xs">
                              {prompt.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded text-gray-700">
                        {prompt.key}
                      </code>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600 capitalize">
                        {PROMPT_CATEGORIES.find(c => c.value === prompt.category)?.label || prompt.category}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={prompt.status} />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <GitBranch className="w-4 h-4" />
                        v{prompt.activeVersion}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(prompt.updatedAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="relative actions-menu">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(openMenuId === prompt.id ? null : prompt.id);
                          }}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <MoreVertical className="w-4 h-4 text-gray-500" />
                        </button>

                        {openMenuId === prompt.id && (
                          <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-dropdown border border-surface-border py-1 z-50">
                            <Link
                              to={`/admin/prompts/${prompt.id}`}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <Edit2 className="w-4 h-4" />
                              Edit
                            </Link>

                            <button
                              onClick={() => {
                                setDuplicateForm({ 
                                  newKey: `${prompt.key}_copy`, 
                                  newName: `${prompt.name} (Copy)` 
                                });
                                setDuplicateModal({ open: true, prompt });
                                setOpenMenuId(null);
                              }}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
                            >
                              <Copy className="w-4 h-4" />
                              Duplicate
                            </button>

                            {prompt.status === 'DRAFT' && (
                              <button
                                onClick={() => {
                                  handleStatusChange(prompt, 'ACTIVE');
                                  setOpenMenuId(null);
                                }}
                                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
                              >
                                <Play className="w-4 h-4" />
                                Publish
                              </button>
                            )}

                            {prompt.status === 'ACTIVE' && (
                              <>
                                {!prompt.isDefault && (
                                  <button
                                    onClick={() => {
                                      handleSetDefault(prompt, true);
                                      setOpenMenuId(null);
                                    }}
                                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
                                  >
                                    <Star className="w-4 h-4" />
                                    Set as Default
                                  </button>
                                )}
                                {prompt.isDefault && (
                                  <button
                                    onClick={() => {
                                      handleSetDefault(prompt, false);
                                      setOpenMenuId(null);
                                    }}
                                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
                                  >
                                    <Star className="w-4 h-4" />
                                    Remove Default
                                  </button>
                                )}
                                <button
                                  onClick={() => {
                                    handleStatusChange(prompt, 'DEPRECATED');
                                    setOpenMenuId(null);
                                  }}
                                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
                                >
                                  <Archive className="w-4 h-4" />
                                  Deprecate
                                </button>
                              </>
                            )}

                            {prompt.status === 'DEPRECATED' && (
                              <button
                                onClick={() => {
                                  handleStatusChange(prompt, 'ACTIVE');
                                  setOpenMenuId(null);
                                }}
                                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
                              >
                                <CheckCircle className="w-4 h-4" />
                                Reactivate
                              </button>
                            )}

                            <div className="border-t border-gray-100 my-1" />

                            {prompt.isArchived ? (
                              <button
                                onClick={() => {
                                  handleRestore(prompt);
                                  setOpenMenuId(null);
                                }}
                                className="flex items-center gap-2 px-4 py-2 text-sm text-green-600 hover:bg-green-50 w-full"
                              >
                                <ArchiveRestore className="w-4 h-4" />
                                Restore
                              </button>
                            ) : (
                              <button
                                onClick={() => {
                                  setDeleteModal({ open: true, prompt });
                                  setOpenMenuId(null);
                                }}
                                className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full"
                              >
                                <Trash2 className="w-4 h-4" />
                                Archive
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </div>

      {/* Create Prompt Modal */}
      <Dialog open={createModal} onOpenChange={setCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Prompt</DialogTitle>
            <DialogDescription>
              Add a new AI prompt to the repository. It will be created as a draft.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="key">Key *</Label>
              <Input
                id="key"
                placeholder="e.g., discovery.goals"
                value={newPrompt.key}
                onChange={(e) => setNewPrompt({ ...newPrompt, key: e.target.value })}
              />
              <p className="text-xs text-gray-500">Unique identifier used to reference this prompt in code</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Discovery Goals Generator"
                value={newPrompt.name}
                onChange={(e) => setNewPrompt({ ...newPrompt, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select
                value={newPrompt.category}
                onValueChange={(v) => setNewPrompt({ ...newPrompt, category: v })}
              >
                <SelectTrigger id="category" className="w-full border-surface-border rounded-lg">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="rounded-lg border-surface-border shadow-dropdown">
                  {PROMPT_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="systemPrompt">System Prompt *</Label>
              <Textarea
                id="systemPrompt"
                placeholder="You are a helpful AI assistant..."
                value={newPrompt.systemPrompt}
                onChange={(e) => setNewPrompt({ ...newPrompt, systemPrompt: e.target.value })}
                rows={6}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={createPrompt.isPending}>
              {createPrompt.isPending ? 'Creating...' : 'Create Prompt'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, prompt: deleteModal.prompt })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive Prompt</DialogTitle>
            <DialogDescription>
              Are you sure you want to archive "{deleteModal.prompt?.name}"? You can restore it later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal({ open: false, prompt: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deletePrompt.isPending}>
              {deletePrompt.isPending ? 'Archiving...' : 'Archive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Modal */}
      <Dialog open={duplicateModal.open} onOpenChange={(open) => setDuplicateModal({ open, prompt: duplicateModal.prompt })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicate Prompt</DialogTitle>
            <DialogDescription>
              Create a copy of "{duplicateModal.prompt?.name}" with a new key.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="newKey">New Key *</Label>
              <Input
                id="newKey"
                placeholder="e.g., discovery.goals_v2"
                value={duplicateForm.newKey}
                onChange={(e) => setDuplicateForm({ ...duplicateForm, newKey: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newName">New Name (optional)</Label>
              <Input
                id="newName"
                placeholder="e.g., Discovery Goals Generator V2"
                value={duplicateForm.newName}
                onChange={(e) => setDuplicateForm({ ...duplicateForm, newName: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateModal({ open: false, prompt: null })}>
              Cancel
            </Button>
            <Button onClick={handleDuplicate} disabled={duplicatePromptMutation.isPending}>
              {duplicatePromptMutation.isPending ? 'Duplicating...' : 'Duplicate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
